import React, { Fragment as F } from 'react'
import c from 'classnames'
import { connect } from 'react-redux'
import get from 'lodash/get'

import Button from 'discada/dist/Button'
import Icon from 'discada/dist/Icon'

import QV, { DTQV } from 'components/Quote/QuoteValue'

import {
  getQuoteValueSum,
  getQuoteValue,
  getAddsProfit,
  getAddsPrice,
  getBestProgramValue,
  getRebatesSum,
  getQuoteDirty,
  getCustomerRebateSumForDealTuner,
  getDealerCashProfit,
  getPaidReserveProfit,
  getQuote,
} from 'selectors/quoteSelectors'

import { getEntity } from 'selectors/entitySelectors'
import { getQueryValue } from 'selectors/pageStateSelectors'

import { addToPageState } from 'actions/pageStateActions'
import { PROFIT_STYLES } from 'reducers/quoteReducers'
import { getHasMdrive } from 'selectors/appStatusSelectors'

import { rateMarkets } from 'utils'

import './DealTuner.scss'

////////////////////////////////////////////////////////////////////
export function TunerButtonPreso({
  section,
  icon,
  children,
  addToPageState,
  activeSection,
  title,
}) {
  let setActiveSection = () => addToPageState({ 'dealtuner.section': section }),
    isActive = activeSection === section

  return (
    <Button
      className={c('tuner-button', { 'tuner-button-active': isActive })}
      icon={icon}
      onClick={setActiveSection}
      size="small"
      title={title || children}
      rounded
    >
      {isActive ? <span>{children}</span> : null}
    </Button>
  )
}

export const TunerButton = connect(
  (state, op) => ({
    ...op,
    activeSection: getQueryValue(state, 'dealtuner.section'),
  }),
  { addToPageState },
)(TunerButtonPreso)

////////////////////////////////////////////////////////////////////

export function TunerSection({ className, children, title, actionComponent }) {
  return (
    <div className={c('deal-tuner-section', className)}>
      <div className="tuner-section-title">
        {title}
        <span style={{ float: 'right' }}>{actionComponent}</span>
      </div>
      <div className="tuner-section-body">{children}</div>
    </div>
  )
}

export function PricingTuner({
  qvProps,
  addsTotalPrice,
  addsProfit,
  daysInStock,
}) {
  return (
    <TunerSection className="pricing-tuner" title="Vehicle">
      <div>
        <DTQV {...qvProps} label="Price" valueKey="price" />

        <QV
          {...qvProps}
          type="number"
          label="Days in Stock"
          value={daysInStock}
          readOnly
        />
      </div>
      <div>
        <DTQV {...qvProps} label="MSRP" valueKey="adjMsrp" />

        {qvProps.isNew ? (
          <DTQV {...qvProps} label="Invoice" valueKey="invoice" />
        ) : (
          <DTQV {...qvProps} label="Cost" valueKey="cost" />
        )}

        <QV {...qvProps} type="number" label="Odometer" valueKey="odometer" />
      </div>

      <div className="profit-set">
        <DTQV
          {...qvProps}
          label="Front Profit"
          valueKey="desiredFrontProfit"
          colorCode
        />
        <DTQV
          {...qvProps}
          label="F&I Purchase"
          value={addsTotalPrice}
          readOnly
        />
        <DTQV
          {...qvProps}
          label="F&I Profit"
          readOnly
          value={addsProfit}
          colorCode
        />
      </div>
    </TunerSection>
  )
}

export function CustomerTuner({
  addToPageState,
  qvProps,
  hasMultiTrades,
  tradeSums,
  hasMdrive,
}) {
  let tradeProfit = tradeSums.acv - tradeSums.allowance,
    singleTradeFields = (
      <F>
        <QV {...qvProps} label="Allowance" valueKey="trades[0].allowance" />
        <QV {...qvProps} label="Payoff" valueKey="trades[0].payoff" />

        <QV
          {...qvProps}
          label="Equity"
          readOnly
          value={tradeSums.allowance - tradeSums.payoff}
        />
      </F>
    ),
    multiTradeFields = (
      <F>
        <QV
          {...qvProps}
          label="Allowance"
          readOnly
          value={tradeSums.allowance}
        />
        <QV {...qvProps} label="Payoff" readOnly value={tradeSums.payoff} />

        <QV
          {...qvProps}
          label="Equity"
          readOnly
          value={tradeSums.allowance - tradeSums.payoff}
        />
      </F>
    )

  return (
    <TunerSection
      className="trade-tuner trade-tuner-multiple-trades"
      title="Customer"
      actionComponent={
        <span
          className="title-control"
          onClick={() => {
            addToPageState({ showTaxConfig: true })
          }}
        >
          Customize Fees & Taxes
        </span>
      }
    >
      <div>
        <QV
          {...qvProps}
          type="text"
          label="Zip Code"
          valueKey="customerZipCode"
          className="zipcode-wrapper"
        />
        <QV
          {...qvProps}
          type="select"
          label={
            <>
              Rate Market <Icon icon="chevron-down" />
            </>
          }
          valueKey="rateMarket"
          className="no-chevron"
          options={{ [-1]: 'Auto', ...rateMarkets }}
        />
        {hasMdrive && (
          <QV
            {...qvProps}
            type="number"
            label="Days to First Pay"
            valueKey="daysToFirstPayment"
          />
        )}
      </div>
      <div>{hasMultiTrades ? multiTradeFields : singleTradeFields}</div>
      <div className="profit-set">
        {hasMultiTrades ? (
          <QV {...qvProps} label="ACV" readOnly value={tradeSums.acv} />
        ) : (
          <QV {...qvProps} label="ACV" valueKey="trades[0].acv" />
        )}

        <QV
          {...qvProps}
          label="Trade Profit"
          readOnly
          value={tradeProfit}
          colorCode
        />
        <QV
          {...qvProps}
          label="Total Incentive"
          readOnly
          type={qvProps.dirty ? 'string' : 'money'}
          value={qvProps.dirty ? '---' : qvProps.customerRebates}
        />
      </div>
    </TunerSection>
  )
}
export function MdriveProfitTuner({ qvProps }) {
  let RateBumpControls = ({ mode }) => (
      <div className="rate-bump-controls">
        <div className="flex-fields">
          <QV
            {...qvProps}
            type="percent"
            label="APR+"
            valueKey={`${mode}AprMarkup`}
          />
          {mode === 'lease' && (
            <QV
              {...qvProps}
              type="decimal"
              label="MF+"
              valueKey="leaseMoneyFactorMarkup"
            />
          )}
          <QV
            {...qvProps}
            type="bool"
            label="Lower?"
            valueKey={`${mode}AcceptLowerMaxRate`}
          />
        </div>
      </div>
    ),
    TargetReserveControls = ({ mode }) => (
      <div>
        <div className="flex-fields">
          <QV
            {...qvProps}
            label="Target"
            valueKey={`${mode}DesiredPaidReserve`}
          />
          <QV
            {...qvProps}
            label="Spread"
            valueKey={`${mode}DesiredPaidReserveRange`}
          />
        </div>
      </div>
    )

  return (
    <TunerSection className="profit-tuner mdrive-profit-tuner" title="Profit">
      <div className="mdrive-inner">
        <div>
          <QV
            {...qvProps}
            type="select"
            label={
              <>
                Lease Profit Style <Icon icon="chevron-down" />
              </>
            }
            className="profit-style-chooser no-chevron"
            valueKey="leaseProfitStyle"
            options={{
              [PROFIT_STYLES.reserve]: 'Target Reserve',
              [PROFIT_STYLES.rateBump]: 'Rate Bump',
            }}
          />
          {qvProps.leaseProfitStyle === PROFIT_STYLES.reserve ? (
            <TargetReserveControls mode="lease" />
          ) : (
            <RateBumpControls mode="lease" />
          )}
        </div>
        <div>
          <QV
            {...qvProps}
            type="select"
            label={
              <>
                Loan Profit Style <Icon icon="chevron-down" />
              </>
            }
            className="profit-style-chooser no-chevron"
            valueKey="loanProfitStyle"
            options={{
              [PROFIT_STYLES.reserve]: 'Target Reserve',
              [PROFIT_STYLES.rateBump]: 'Rate Bump',
            }}
          />
          {qvProps.loanProfitStyle === PROFIT_STYLES.reserve ? (
            <TargetReserveControls mode="loan" />
          ) : (
            <RateBumpControls mode="loan" />
          )}
        </div>
      </div>
      <div className="profit-set">
        <QV
          {...qvProps}
          label="Dealer Cash"
          readOnly
          type={qvProps.dirty ? 'string' : 'money'}
          value={qvProps.dirty ? '---' : qvProps.dealerCash}
        />
        <QV
          {...qvProps}
          label="Reserve"
          readOnly
          colorCode
          type={qvProps.dirty ? 'string' : 'money'}
          value={qvProps.dirty ? '---' : qvProps.paidReserve}
        />
        <QV
          {...qvProps}
          label="Total Profit"
          readOnly
          type={qvProps.dirty ? 'string' : 'money'}
          value={qvProps.dirty ? '---' : qvProps.totalProfit}
          colorCode
        />
      </div>
    </TunerSection>
  )
}

export function ProfitTuner({ qvProps }) {
  return (
    <TunerSection className="profit-tuner" title="Profit">
      <div>
        <QV
          {...qvProps}
          type="select"
          label="Profit Style"
          className="profit-style-chooser"
          valueKey="profitStyle"
          options={{
            [PROFIT_STYLES.reserve]: 'Target Reserve',
            [PROFIT_STYLES.rateBump]: 'Rate Bump',
          }}
        />
      </div>
      <div>
        {qvProps.profitStyle === PROFIT_STYLES.reserve ? (
          <F>
            <QV
              {...qvProps}
              label="Target Reserve"
              valueKey="desiredPaidReserve"
            />
            <QV
              {...qvProps}
              label="Target Reserve +/-"
              valueKey="desiredPaidReserveRange"
            />
          </F>
        ) : (
          <F>
            <QV {...qvProps} type="percent" label="APR" valueKey="aprMarkup" />
            <QV
              {...qvProps}
              type="decimal"
              label="Money Factor"
              valueKey="moneyFactorMarkup"
            />
            <QV
              {...qvProps}
              type="bool"
              label="Accept Lower"
              valueKey="acceptLowerMaxRate"
            />
          </F>
        )}
      </div>
      <div className="profit-set">
        <QV
          {...qvProps}
          label="Dealer Cash"
          readOnly
          type={qvProps.dirty ? 'string' : 'money'}
          value={qvProps.dirty ? '---' : qvProps.dealerCash}
        />
        <QV
          {...qvProps}
          label="Reserve"
          readOnly
          colorCode
          type={qvProps.dirty ? 'string' : 'money'}
          value={qvProps.dirty ? '---' : qvProps.paidReserve}
        />
        <QV
          {...qvProps}
          label="Total Profit"
          readOnly
          type={qvProps.dirty ? 'string' : 'money'}
          value={qvProps.dirty ? '---' : qvProps.totalProfit}
          colorCode
        />
      </div>
    </TunerSection>
  )
}

export function DealTuner(props) {
  let sectionProps = {
      ...props,
      setActiveSection: s => () =>
        props.addToPageState({ 'dealtuner.section': s }),
    },
    { hasMdrive } = props

  return (
    <div className={c('deal-tuner')}>
      <PricingTuner {...sectionProps} />
      <CustomerTuner {...sectionProps} />
      {hasMdrive ? (
        <MdriveProfitTuner {...sectionProps} />
      ) : (
        <ProfitTuner {...sectionProps} />
      )}
    </div>
  )
}

function mapStateToProps(state, op) {
  let vehicle = getEntity(state, op.vehicleId),
    quote = getQuote(state, op.vehicleId, op.quoteId),
    isNew = get(vehicle, 'type') === 'New',
    daysInStock = get(vehicle, 'daysInStock'),
    vehicleProfit = getQuoteValue(
      state,
      op.vehicleId,
      op.quoteId,
      'desiredFrontProfit',
      0,
    ),
    addsProfit = getAddsProfit(state, op.vehicleId, op.quoteId),
    paidReserve = getBestProgramValue(
      state,
      op.vehicleId,
      op.quoteId,
      op.mode,
      'PaidReserve',
    ),
    tradeSums = {
      allowance: getQuoteValueSum(
        state,
        op.vehicleId,
        op.quoteId,
        'trades',
        'allowance',
      ),
      acv: getQuoteValueSum(state, op.vehicleId, op.quoteId, 'trades', 'acv'),
      payoff: getQuoteValueSum(
        state,
        op.vehicleId,
        op.quoteId,
        'trades',
        'payoff',
      ),
    },
    tradeProfit = tradeSums.acv - tradeSums.allowance,
    dealerCash = getRebatesSum(state, op.vehicleId, op.quoteId, op.mode, true),
    totalProfit =
      vehicleProfit +
      addsProfit +
      tradeProfit +
      getPaidReserveProfit(state, op.vehicleId, op.quoteId, op.mode) +
      getDealerCashProfit(state, op.vehicleId, op.quoteId, op.mode)

  return {
    ...op,
    qvProps: {
      vehicleId: op.vehicleId,
      quoteId: op.quoteId,
      ...quote,
      permit: 'profit',
      dirty: getQuoteDirty(state, op.vehicleId, op.quoteId),
      price: getQuoteValue(state, op.vehicleId, op.quoteId, 'price'),
      odometer: getQuoteValue(state, op.vehicleId, op.quoteId, 'odometer'),
      type: 'money',
      isNew,
      paidReserve,
      totalProfit,
      customerRebates: getCustomerRebateSumForDealTuner(
        state,
        op.vehicleId,
        op.quoteId,
        op.mode,
      ),
      dealerCash,
      profitStyle: getQuoteValue(
        state,
        op.vehicleId,
        op.quoteId,
        'profitStyle',
        PROFIT_STYLES.reserve,
      ),
    },
    vehicleProfit,
    daysInStock,
    addsProfit,
    addsTotalPrice: getAddsPrice(state, op.vehicleId, op.quoteId),
    hasMultiTrades:
      getQuoteValue(state, op.vehicleId, op.quoteId, 'trades', []).length > 1,
    tradeSums,
    hasMdrive: getHasMdrive(state),
  }
}

export default connect(
  mapStateToProps,
  { addToPageState },
)(DealTuner)
